import { useCallback } from 'react';
import { useStoreStore } from '@/stores/storeStore';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib';
import { buildAddFunnelToStoreUrl, genFunnelURL } from '../logic';

interface AddFunnelInput {
    storeId: number;
    slug: string;
    name: string;
    url?: string;
    image?: string;
}

/**
 * Hop the user from the React funnels surface into the legacy Vue
 * URL/Media product-create page, with the funnel's URL/title/image
 * pre-seeded as query params. The Vue PageLinkURL component reads
 * those params and uses them as initial form values.
 *
 * My Store hasn't been migrated to React yet — the redirect is full-
 * page so the legacy bundle picks up. Once My Store moves over, this
 * hook is the only place that needs to change.
 */
export function useAddFunnelToStore() {
    const { user } = useUser();
    const linksiteStoreId = useStoreStore(state => state.defaultStoreId);

    return useCallback(
        (funnel: AddFunnelInput) => {
            if (!user?.username) {
                stanNotify({ type: 'error', title: 'Could not add to store', text: 'Please reload and try again.' });
                return;
            }
            if (!linksiteStoreId) {
                stanNotify({
                    type: 'error',
                    title: 'No Stan store yet',
                    text: 'Set up your Stan store first, then try again.',
                });
                return;
            }

            const funnelUrl = funnel.url ?? genFunnelURL(funnel.slug, user.username);

            const url = buildAddFunnelToStoreUrl({
                storeId: linksiteStoreId,
                funnelId: funnel.storeId,
                funnelUrl,
                funnelName: funnel.name,
                funnelImage: funnel.image,
            });

            // Full-page redirect — destination lives in the legacy Vue bundle.
            window.location.href = url;
        },
        [linksiteStoreId, user?.username]
    );
}
